<template>
  <div class="min-h-screen bg-primary w-full h-full mt-[60px] lg:mt-0 themeTransition">
    <Transition
      enter-active-class="duration-1000 page-enter-transition"
      leave-active-class="duration-300 page-leave-transition"
      enter-from-class="page-enter-from"
      leave-to-class="page-enter-from"
    >
      <div v-if="!isLoading">
        <div class="slider fixed h-screen w-screen bg-orange z-10" />
        <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
          <!-- content begin -->
          <project-hero v-bind="{ ...projectHero }" />
          <section class="sectionContainer mb-16 lg:min-h-full lg:mb-32">
            <div :class="sectionHeaderStyle">
              <header2
                v-if="aboutLabel"
                :text="aboutLabel"
                classes="mb-10 md:col-span-8 lg:col-span-6 md:mb-12 text-base"
                :is-umbraco="isUmbraco"
                :theme="themeType"
              />
            </div>
            <div :class="sectionStyle">
              <div v-if="pageProperties?.challenge.markup">
                <header3
                  v-if="challengesLabel"
                  :text="challengesLabel"
                  classes="mb-3 md:col-span-8 lg:col-span-6 text-m"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                />
                <rich-text
                  v-if="pageProperties?.challenge?.markup"
                  :text="pageProperties?.challenge?.markup"
                  :isUmbraco="isUmbraco"
                  :theme="themeType"
                  :isManchet="false"
                  classes="md:col-span-6 mb-10 md:mb-12"
                />
              </div>
              <div v-if="pageProperties?.solution.markup">
                <header3
                  v-if="solutionLabel"
                  :text="solutionLabel"
                  classes="mb-3 md:col-span-8 lg:col-span-6 text-m"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                />
                <rich-text
                  v-if="pageProperties?.solution?.markup"
                  :text="pageProperties?.solution?.markup"
                  :isUmbraco="isUmbraco"
                  :theme="themeType"
                  :isManchet="false"
                  classes="md:col-span-6 mb-10 md:mb-12"
                />
              </div>
              <div v-if="pageProperties?.solution.markup">
                <header3
                  v-if="valueLabel"
                  :text="valueLabel"
                  classes="mb-3 md:col-span-8 lg:col-span-6 text-m"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                />
                <rich-text
                  v-if="pageProperties?.value?.markup"
                  :text="pageProperties?.value?.markup"
                  :isUmbraco="isUmbraco"
                  :theme="themeType"
                  :isManchet="false"
                  classes="md:col-span-6 mb-10 md:mb-12"
                />
              </div>
            </div>
          </section>
          <section class="sectionContainer mb-5 lg:min-h-full lg:mb-9">
            <div :class="sectionHeaderStyle">
              <header2
                v-if="specificationLabel"
                :text="specificationLabel"
                classes="mb-10 md:col-span-8 lg:col-span-6 md:mb-12 text-base"
                :is-umbraco="isUmbraco"
                :theme="themeType"
              />
            </div>
            <div
              :class="[
                sectionStyle,
                'relative',
                'flex',
                'flex-wrap',
                'items-stretch',
                'gap-x-5',
                'lg:gap-x-10',
              ]"
            >
              <div
                v-for="item in pageData?.properties?.projectSpecifications?.items"
                :class="blockStyle"
              >
                <project-specification
                  v-if="item.content.properties.title"
                  :title="item.content.properties.title"
                  :description="item.content.properties.description.markup"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                  :is-richtext="true"
                />
              </div>
              <div :class="blockStyle">
                <project-specification
                  v-if="pageProperties?.location"
                  :title="locationLabel"
                  :description="pageProperties?.location"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                />
              </div>

              <div :class="blockStyle">
                <project-specification
                  v-if="pageProperties?.periode"
                  :title="periodeLabel"
                  :description="pageProperties?.periode"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                />
              </div>
              <div :class="blockStyle">
                <project-specification
                  v-if="pageProperties?.country"
                  :title="countryLabel"
                  :description="pageProperties?.country"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                />
              </div>

              <div :class="blockStyle">
                <project-specification
                  v-if="pageProperties?.partners"
                  :title="partnersLabel"
                  :description="pageProperties?.partners"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                />
              </div>

              <div :class="blockStyle">
                <project-specification
                  v-if="pageProperties?.services"
                  :title="servicesLabel"
                  :description="pageProperties?.services"
                  :is-umbraco="isUmbraco"
                  :theme="themeType"
                />
              </div>
            </div>
          </section>

          <div id="content" class="col-span-full" v-if="sections && sections.length > 0">
            <component
              v-for="(section, index) in sections"
              :key="index"
              :is="getComponent(section?.name as ComponentType)"
              :ref="section?.name"
              v-bind="{ ...section?.props }"
            ></component>
          </div>
          <related-projects
            v-if="relatedProjectsSection?.projects"
            :title="relatedProjectsSection?.title"
            :cta-label="relatedProjectsSection?.ctaLabel"
            :cta-link="relatedProjectsSection?.ctaLink"
            :target="relatedProjectsSection?.target"
            :projects="relatedProjectsSection?.projects"
          />
          <!-- content end -->
        </page-wrapper>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import { parseUrl, changeTheme, getThemeType } from "@/util/helper";
import {
  getPage,
  getPageByID,
  getRelatedItemsLink,
  getRelatedProjects,
  getTranslations,
} from "@/util/api";
import { IProjectArticlePage } from "@/types/IProjectArticlePage";
import PageWrapper from "@components/PageWrapper.vue";
import ProjectHero, {
  ProjectHeroSectionProps,
} from "@organisms/ProjectHero/ProjectHero.ce.vue";
import { IRelatedLinkProps, Theme } from "@/types/types";
import { getComponentData, getComponent } from "@/util/getComponent";
import { ComponentType, RelatedItemsType } from "@/util/constants";
import Header2 from "@/components/Typography/Header2.ce.vue";
import Header3 from "@/components/Typography/Header3.ce.vue";
import RichText from "@/components/Typography/RichText.ce.vue";
import ProjectSpecification from "@molecules/ProjectSpecification/ProjectSpecification.ce.vue";
import RelatedProjects from "@organisms/RelatedProjects/RelatedProjects.ce.vue";
import { getPathForPageData } from "@/util/getPathForPageData";

const isLoading = ref(true);
const isUmbraco = ref(false);
const pageData = ref<IProjectArticlePage | null>(null);
const pageGUI = ref<string | undefined>();
const currentGUID = ref<string | undefined>();
const theme = ref<Theme | null>(null);
const relatedProjects = ref<IProjectArticlePage[] | null>(null);
const relatedProjectsLink = ref<IRelatedLinkProps | null>(null);
const shareLabel = ref<string>("");
const aboutLabel = ref<string>("");
const challengesLabel = ref<string>("");
const solutionLabel = ref<string>("");
const valueLabel = ref<string>("");
const specificationLabel = ref<string>("");
const locationLabel = ref<string>("");
const countryLabel = ref<string>("");
const periodeLabel = ref<string>("");
const partnersLabel = ref<string>("");
const servicesLabel = ref<string>("");
const pageKey = ref<string | undefined>();

const fetchInitialData = async () => {
  pageKey.value = document.documentElement.dataset.id;
  const pageParams = getPathForPageData();
  if (!pageParams?.path || !pageKey.value) return;
  [
    pageData.value,
    relatedProjectsLink.value,
    aboutLabel.value,
    shareLabel.value,
    challengesLabel.value,
    solutionLabel.value,
    valueLabel.value,
    specificationLabel.value,
  ] = await Promise.all([
    getPageByID(pageKey.value, pageParams?.isPreview) as Promise<IProjectArticlePage>,
    //getPage(pageParams?.path, pageParams?.isPreview) as Promise<IProjectArticlePage>,
    getRelatedItemsLink(RelatedItemsType.PROJECT) as Promise<IRelatedLinkProps>,
    getTranslations("ProjectAbout"),
    getTranslations("ShareTitle"),
    getTranslations("ProjectChallenge"),
    getTranslations("ProjectSolution"),
    getTranslations("ProjectValue"),
    getTranslations("ProjectSpecificationsHeader"),
  ]);
  [
    locationLabel.value,
    countryLabel.value,
    periodeLabel.value,
    partnersLabel.value,
    servicesLabel.value,
  ] = await Promise.all([
    getTranslations("ProjectLocation"),
    getTranslations("ProjectCountry"),
    getTranslations("ProjectPeriode"),
    getTranslations("ProjectPartners"),
    getTranslations("ProjectServices"),
  ]);
  await handlePageData();
  await loadRelatedProjects();
};

const handlePageData = async () => {
  if (!pageData.value) return;
  pageGUI.value = pageData.value.route.startItem.id;
  updateTheme();
  isLoading.value = false;
};

const updateTheme = () => {
  if (pageProperties.value) {
    const backgroundColor = pageProperties.value.backgroundColor;
    theme.value = backgroundColor ? backgroundColor : theme.value;
  }
  if (theme.value) changeTheme(theme.value);
};

const loadRelatedProjects = async () => {
  if (!pageData.value) return;
  currentGUID.value = pageData.value.id;
  const sectorGUID = pageData.value.properties.projectSector[0].id;
  relatedProjects.value = await getRelatedProjects(sectorGUID, currentGUID.value);
};

onBeforeMount(fetchInitialData);

const pageProperties = computed(() => {
  return pageData.value?.properties;
});

const projectHero = computed(() => {
  if (!pageProperties.value) return;
  return {
    title: pageProperties.value.title,
    imageAltText: pageProperties.value.imageAltText,
    theme: theme.value,
    image: pageProperties.value.image[0],
    partners: pageProperties.value.tag,
    classes: "font-bold",
  } as ProjectHeroSectionProps;
});

const sections = computed(() => {
  if (!pageData.value?.properties.grid) return [];
  const sections = pageData.value?.properties.grid.items.map((item) => {
    const { content } = item;
    if (!content) return;
    return getComponentData(content, theme.value);
  });

  const sanitizedSections = sections.filter(Boolean);
  return sanitizedSections;
});

const relatedProjectsSection = computed(() => {
  if (!relatedProjects.value || !relatedProjectsLink.value) return;
  const projects = relatedProjects.value.map((project) => {
    const { properties } = project;
    return {
      title: properties.title,
      tag: properties.tag,
      country: properties.country,
      image: JSON.stringify(properties.image[0]),
      imgAlt: properties.imageAltText,
      client: properties.partners,
      url: project.route.path,
    };
  });
  const projectArray = projects.length > 0 ? JSON.stringify(projects) : undefined;
  return {
    theme: theme.value,
    title: relatedProjectsLink.value.pageText,
    projects: projectArray,
    ctaLabel: relatedProjectsLink.value.seeAllName,
    ctaLink: relatedProjectsLink.value.seeAllUrl,
    target: "_self",
  };
});

const themeType = computed(() => {
  if (!theme.value) return "dark";
  return getThemeType(theme.value);
});

const blockStyle = computed(() => {
  return `relative flex-25 lg:flex-50 mb-5 lg:mb-10`;
});

const sectionHeaderStyle = computed(() => {
  return `col-span-12 lg:col-span-3 col-start-2 lg:col-start-2`;
});

const sectionStyle = computed(() => {
  return `col-span-12 col-start-2 lg:col-span-6 lg:col-start-5`;
});
</script>
